/**
 * Masse, Schriftbreiten-Schaetzung und Farben des Zeitstrahls — ohne React,
 * damit ProjectGantt.tsx nur Komponenten exportiert (Fast Refresh).
 */
// ── Zeitstrahl-Geometrie ───────────────────────────────────────────────────
// Das Diagramm rechnet in echten Pixeln: die viewBox ist so breit wie der
// gemessene Platz, eine "11px"-Schrift bleibt also auf jedem Schirm 11px.
// Frueher war die viewBox fest 740 breit und wurde auf die Kachel skaliert —
// im Vollbild wuchs dadurch nur die Schrift, nicht die Zeitachse. Jetzt waechst
// die Zeitachse mit, die Namensspalte in Grenzen (22 %, 204..360 px).
// Die Konstanten sind die Grundmasse; `ganttLayout` leitet daraus die
// tatsaechliche Geometrie fuer eine Breite und (im Vollbild) eine Hoehe ab.
export const G_W = 740;        // Grundbreite, solange noch nichts gemessen ist
export const G_LABEL = 204;    // Mindestbreite der Projektspalte links
export const G_TRACK = 526;    // Zeitachse bei Grundbreite (G_LABEL + G_TRACK + G_RIGHT = G_W)
export const G_RIGHT = 10;     // Luft rechts hinter der Zeitachse
export const G_HEAD = 34;      // Hoehe der Monatsleiste: oben Monatsnamen, unten Wochenstriche + Heute-Plakette
export const G_MONTH_Y = 14;   // Grundlinie der Monatsnamen (obere Spur)
export const G_ROW = 34;       // Hoehe einer Projektzeile (Normalmass)
export const G_PAD = 8;        // Innenabstand der Projektspalte (links wie rechts)
export const G_GAP = 6;        // Luft zwischen Spaltentrenner und Balkenanfang

/** Unterhalb dieser Breite scrollt der Container waagrecht, statt zu quetschen. */
export const G_MIN_W = 720;
/** Die Projektspalte waechst bis hierhin mit, damit lange Namen ganz lesbar sind. */
export const G_LABEL_MAX = 360;
export const G_LABEL_SHARE = 0.22;
/** Obergrenze der Zeilenhoehe im Vollbild — darueber wirken wenige Zeilen verloren. */
export const G_ROW_MAX = 52;
/** Luft ueber der ersten und unter der letzten Zeile (4 oben, 2 unten). */
export const G_BODY_TOP = 4;
export const G_BODY_PAD = 6;

export interface GanttLayoutInput {
    /** Gemessene Innenbreite des Containers in px; 0/ungueltig = Grundbreite. */
    width: number;
    /** Gemessene Innenhoehe (nur im Vollbild von Belang). */
    availableHeight?: number;
    rowCount: number;
    fullscreen: boolean;
}

export interface GanttLayout {
    /** Breite des SVG = Breite der viewBox. */
    width: number;
    /** Breite der Projektspalte. */
    label: number;
    /** Breite der Zeitachse. */
    track: number;
    /** Hoehe der Monatsleiste. */
    head: number;
    /** Hoehe einer Projektzeile. */
    row: number;
    /** Gesamthoehe: Monatsleiste + Zeilen + Luft. */
    height: number;
    /** Hoehe des Zeilenbereichs unter der Monatsleiste. */
    bodyHeight: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Geometrie des Zeitstrahls fuer eine gemessene Flaeche.
 *
 * - Breite: nie unter G_MIN_W; alles darueber geht in die Zeitachse.
 * - Projektspalte: 22 % der Breite, geklemmt auf G_LABEL..G_LABEL_MAX.
 * - Zeilen: im Vollbild so hoch, dass alle Zeilen die Flaeche fuellen —
 *   aber nur, wenn sie im Normalmass ueberhaupt passen, und nie ueber
 *   G_ROW_MAX. Passen sie nicht, bleibt es beim Normalmass, und der
 *   Container scrollt senkrecht.
 */
export const ganttLayout = ({ width, availableHeight, rowCount, fullscreen }: GanttLayoutInput): GanttLayout => {
    const measured = Number.isFinite(width) && width > 0 ? Math.floor(width) : G_W;
    const w = Math.max(G_MIN_W, measured);
    const label = Math.round(clamp(w * G_LABEL_SHARE, G_LABEL, G_LABEL_MAX));
    const track = w - label - G_RIGHT;
    const rows = Math.max(0, Math.floor(rowCount));
    let row = G_ROW;
    if (fullscreen && rows > 0 && availableHeight !== undefined && Number.isFinite(availableHeight)) {
        const fit = Math.floor((availableHeight - G_HEAD - G_BODY_PAD) / rows);
        if (fit > G_ROW) row = Math.min(G_ROW_MAX, fit);
    }
    const bodyHeight = G_BODY_PAD + rows * row;
    return { width: w, label, track, head: G_HEAD, row, height: G_HEAD + bodyHeight, bodyHeight };
};

/**
 * Lage der Inhalte in einer Zeile der Hoehe `row`, relativ zur Zeilenoberkante.
 * Beim Normalmass (34) exakt die alten Werte; waechst die Zeile, wandert der
 * Inhalt in die Mitte und der Balken wird etwas kraeftiger (hoechstens 28).
 */
export const ganttRowBox = (row: number) => {
    const shift = Math.max(0, (row - G_ROW) / 2);
    const barH = Math.round(clamp(20 + (row - G_ROW) * 0.45, 20, 28));
    return {
        barY: Math.round((row - barH) / 2),
        barH,
        nameY: 14 + shift,
        metaY: 27 + shift,
        tagY: 3.5 + shift,
        center: row / 2,
    };
};

/** Bezugsjahr der Achse: 1. Januar bis 31. Dezember des Jahres von `todayMs`. */
export const ganttYear = (todayMs: number) => {
    const year = new Date(todayMs).getFullYear();
    const start = new Date(year, 0, 1).getTime();
    const end = new Date(year, 11, 31).getTime();
    return { year, start, end, range: end - start };
};

/**
 * Ganze Kalendertage von heute bis `date` (negativ = vorbei), fuer die
 * Hover-Karte („heute", „4 Tage überfällig").
 *
 * `parseDateLocal` setzt jedes Datum auf 12:00. Gegen „heute 00:00"
 * gerechnet blieb ein halber Tag stehen, und `Math.round` rundete ihn
 * falsch: heute faellig wurde „in 1 Tagen", 4 Tage ueberfaellig wurde 3.
 * Deshalb stehen beide Seiten auf 12:00 — Mittag gegen Mittag bleibt auch
 * ueber die Zeitumstellung (23/25-Stunden-Tag) eine ganze Zahl.
 */
export const daysFromToday = (date: Date | null | undefined, todayMs: number): number | null => {
    if (!date || !Number.isFinite(date.getTime())) return null;
    const today = new Date(todayMs); today.setHours(12, 0, 0, 0);
    const day = new Date(date.getTime()); day.setHours(12, 0, 0, 0);
    return Math.round((day.getTime() - today.getTime()) / 864e5);
};

/** Bruchteil des Jahres (0..1, ausserhalb moeglich) → x in der viewBox. */
export const fracToX = (l: Pick<GanttLayout, 'label' | 'track'>, frac: number) => l.label + frac * l.track;

/** Zeitpunkt → x in der viewBox. */
export const timeToX = (l: Pick<GanttLayout, 'label' | 'track'>, t: number, axis: { start: number; range: number }) =>
    fracToX(l, (t - axis.start) / axis.range);

/**
 * Balken eines Projekts. Er beginnt erst hinter dem Spaltentrenner (sonst
 * klebt er am Namen) und ist mindestens 6 px breit.
 */
export const ganttBar = (
    l: Pick<GanttLayout, 'label' | 'track'>,
    startMs: number | null,
    endMs: number | null,
    axis: { start: number; range: number },
) => {
    const rawStart = startMs !== null ? timeToX(l, startMs, axis) : l.label;
    const rawEnd = endMs !== null ? timeToX(l, endMs, axis) : l.label + l.track;
    const x = Math.max(l.label + G_GAP, rawStart);
    const end = Math.max(x + 6, rawEnd);
    return { x, width: end - x };
};

/** Masse der „Heute"-Plakette; sie sitzt in der unteren Spur der Monatsleiste,
 *  damit sie keinen Monatsnamen verdeckt. */
export const TODAY_BADGE_W = 40;
export const TODAY_BADGE_H = 15;

/** Die Plakette darf weder in die Namensspalte noch ueber den rechten Rand rutschen. */
export const todayBadgeX = (l: Pick<GanttLayout, 'label' | 'width'>, todayX: number) =>
    Math.min(Math.max(todayX - TODAY_BADGE_W / 2, l.label + 2), l.width - TODAY_BADGE_W - 2);

const MONTH_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const MONTH_LONG = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

/**
 * Lage der Montage des Achsenjahres als Bruchteil (0..1) — fuer die
 * Wochenstriche unter den Monatsnamen.
 */
export const weekFractions = (axis: { start: number; range: number }) => {
    const out: number[] = [];
    const d = new Date(axis.start);
    d.setDate(d.getDate() + ((8 - d.getDay()) % 7)); // erster Montag
    for (; d.getTime() <= axis.start + axis.range; d.setDate(d.getDate() + 7)) out.push((d.getTime() - axis.start) / axis.range);
    return out;
};

/** Monatsbeschriftung passend zur Spaltenbreite: J / Jan / Januar. */
export const monthLabel = (i: number, monthWidth: number) =>
    monthWidth >= 96 ? MONTH_LONG[i] : monthWidth >= 34 ? MONTH_SHORT[i] : MONTH_SHORT[i].charAt(0);

// SVG kennt kein `text-overflow`, darum wird der Text vorher gekuerzt. Im
// Browser misst ProjectGantt mit der echten Schrift (Canvas); diese Schaetzung
// ist nur der Rueckfall ohne Canvas (Tests, sehr alte Browser). Die Faktoren
// sind Mittelwerte der UI-Schrift; zusaetzlich wird auf die Spalte geclippt.
export type TextMeasure = (s: string, fontSize: number) => number;
const NARROW_CHARS = ' .,:;!|\'"()[]{}-·ijlrtfI';
const WIDE_CHARS = 'mwMW@%';
export const approxTextWidth = (s: string, fontSize: number) => {
    let units = 0;
    for (const ch of s) {
        if (NARROW_CHARS.includes(ch)) units += 0.33;
        else if (WIDE_CHARS.includes(ch)) units += 0.88;
        else if (ch >= 'A' && ch <= 'Z') units += 0.66;
        else units += 0.54;
    }
    return units * fontSize;
};
/**
 * Text auf `maxWidth` kuerzen, mit „…" am Ende. Binaersuche nach dem
 * laengsten Anfang, der samt „…" passt (mindestens ein Zeichen) — bei hundert
 * Zeilen und echter Messung waeren Zeichen-fuer-Zeichen-Schleifen zu teuer.
 */
export const fitText = (s: string, maxWidth: number, fontSize: number, measure: TextMeasure = approxTextWidth) => {
    if (measure(s, fontSize) <= maxWidth) return s;
    const chars = Array.from(s);
    let lo = 1, hi = chars.length - 1, best = 1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (measure(chars.slice(0, mid).join('') + '…', fontSize) <= maxWidth) { best = mid; lo = mid + 1; }
        else hi = mid - 1;
    }
    return chars.slice(0, best).join('').replace(/[\s·⇄]+$/, '') + '…';
};

// Balkenfarben. Als Konstanten, weil Legende und Balken sie beide brauchen und
// sonst beim naechsten Palettenwechsel auseinanderlaufen.
export const G_COL = {
    low: '#ea9c0b',
    mid: '#2f83f6',
    high: '#0d9f72',
    planning: '#56a2fb',
    onHold: '#fb923c',
    today: '#dd2b2b',
} as const;
